import { useEffect, useState } from "react";
import axios from "../../api/axiosInstance";
import { API_BASE_URL } from "../../config";

interface Endpoint {
    method: string;
    path: string;
    description: string;
    fields?: string[];
}

interface ApiKeyResponse {
    api_key: string | null;
    plan: string;
    is_demo: boolean;
}
interface RegenerateKeyResponse {
    message: string;
    api_key: string;
}

const endpoints: Endpoint[] = [
    { method: "GET", path: "/api_access/menu", description: "Fetch all menu items" },
    { method: "POST", path: "/api_access/menu", description: "Create a new menu item", fields: ["name", "description", "price"] },
    { method: "PUT", path: "/api_access/menu/:id", description: "Update an existing menu item", fields: ["name", "description", "price"] },
    { method: "DELETE", path: "/api_access/menu/:id", description: "Delete a menu item" },
];

export default function AdminApiDocs() {
    const [apiKey, setApiKey] = useState<string | null>(null);
    const [plan, setPlan] = useState("");
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [isDemo, setIsDemo] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

    const fetchApiKey = async () => {
        try {
            const res = await axios.get<ApiKeyResponse>("/api_access/api_key");
            setApiKey(res.data.api_key);
            setPlan(res.data.plan);
            setIsDemo(res.data.is_demo);
        } catch (err) {
            console.error("Failed to fetch API key", err);
        }
    };

    useEffect(() => {
        fetchApiKey();
    }, []);

    const handleRegenerateKey = async () => {
        setRegenerating(true);
        try {
            const res = await axios.post<RegenerateKeyResponse>("/api_access/api_key/regenerate");
            setApiKey(res.data.api_key);
        } catch (err) {
            console.error("Failed to regenerate API key", err);
        } finally {
            setRegenerating(false);
        }
    };

    const handleCopy = async (fullUrl: string, idx: number) => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            setCopiedIndex(idx);
            setTimeout(() => setCopiedIndex(null), 1500);
        } catch (err) {
            console.error("Failed to copy to clipboard", err);
        }
    };

    if (plan !== "Enterprise" && !isDemo) {
        return (
            <div className="p-6 text-center text-red-500 font-semibold">
                ❌ This page is only available to Enterprise plan users.
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">🔐 API Access (Enterprise Only)</h2>
            <p className="mb-4 text-gray-700">
                Use the following API endpoints with your API key in the <code className="bg-gray-200 px-2 py-1 rounded text-sm">X-API-Key</code> header.
            </p>

            <div className="bg-white shadow p-4 rounded border mb-6">
                <h4 className="text-lg font-semibold mb-1 text-gray-800">Your API Key</h4>
                <code className="block text-blue-600 bg-gray-100 px-4 py-2 rounded break-all mb-3">{apiKey}</code>
                <button
                    onClick={handleRegenerateKey}
                    disabled={regenerating}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded transition disabled:opacity-50"
                >
                    {regenerating ? "Regenerating..." : "Regenerate Key"}
                </button>
            </div>

            {endpoints.map((ep, idx) => {
                const fullUrl = `${API_BASE_URL}${ep.path}`;
                return (
                    <div
                        key={idx}
                        className="bg-gray-100 p-4 rounded shadow mb-4 transition hover:bg-gray-200"
                    >
                        <div className="flex justify-between items-start">
                            <div className="cursor-pointer" onClick={() => setExpandedIndex(idx === expandedIndex ? null : idx)}>
                                <h4 className="text-lg font-semibold">
                                    {ep.method} <span className="text-blue-600">{ep.path}</span>
                                    <p className="text-sm text-gray-600">Click to expand</p>
                                </h4>
                                <p className="text-sm text-gray-600">{ep.description}</p>
                            </div>
                            <button
                                onClick={() => handleCopy(fullUrl, idx)}
                                className="text-sm text-blue-700 hover:underline ml-4 mt-1"
                            >
                                📋 {copiedIndex === idx ? "Copied!" : "Copy URL"}
                            </button>
                        </div>

                        {expandedIndex === idx && (
                            <div className="mt-4 text-sm text-gray-700">
                                {ep.fields && (
                                    <div className="mb-2">
                                        <strong>Required Fields:</strong> {ep.fields.join(", ")}
                                    </div>
                                )}
                                <div className="mb-2">
                                    <strong>Example Request:</strong>
                                    <pre className="bg-white p-2 mt-1 border rounded overflow-x-auto text-xs">
{`fetch("${fullUrl}", {
  method: "${ep.method}",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": "${apiKey}"
  }${ep.method === "GET" || ep.method === "DELETE" ? "" : `,
  body: JSON.stringify({
    name: "Pizza",
    description: "Tasty",
    price: 10
  })`}
})`}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
